import { useEffect, useState } from "react";
import { setRecoveryEmail, type Account } from "../api.js";

interface Props {
  account: Account;
  /** La cuenta cambió: el llamador refresca lo que muestre el correo. */
  onAccountChange: (account: Account) => void;
  onClose: () => void;
}

/**
 * Configurar el correo de recuperación desde dentro del juego.
 *
 * Existe porque el registro no puede ser el único sitio donde se da: cuando esto se
 * construyó ya había cuentas jugando, y ninguna tenía correo. Sin esta pantalla, la
 * recuperación por correo sólo serviría para quien se registrara a partir de hoy — y
 * quien ya perdió su contraseña, que es el caso que la motivó, seguiría sin salida.
 *
 * Pide la contraseña aunque ya haya sesión, y el motivo está en el servidor: es lo único
 * que separa "el dueño configuró su recuperación" de "quien encontró la sesión abierta
 * apuntó la recuperación a su buzón". Ver `POST /api/email` en `api/auth.ts`.
 */
export function AccountPanel({ account, onAccountChange, onClose }: Props) {
  const [email, setEmail] = useState(account.email ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);

    try {
      const result = await setRecoveryEmail(email.trim(), password);
      onAccountChange(result.player);
      setPassword("");
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy;

  return (
    <div className="overlay">
      <div className="panel sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Tu cuenta</h2>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <p className="sheet__state">
          {account.email === null ? (
            <>
              No tienes correo de recuperación. Sin él, si olvidas tu contraseña no hay
              forma automática de devolverte la cuenta.
            </>
          ) : account.emailVerified ? (
            <>
              Recuperación activa en <strong>{account.email}</strong>.
            </>
          ) : (
            <>
              <strong>{account.email}</strong> está guardado pero <strong>sin confirmar</strong>,
              así que todavía no sirve para recuperar. Abre el enlace que te llegó, o vuelve
              a pedirlo desde aquí.
            </>
          )}
        </p>

        <form
          className="account__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) void submit();
          }}
        >
          <label className="auth__field">
            <span>Correo</span>
            <input
              className="join__input"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setDone(false);
              }}
              autoComplete="email"
              inputMode="email"
              disabled={busy}
            />
          </label>

          <label className="auth__field">
            <span>Tu contraseña actual</span>
            <input
              className="join__input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              disabled={busy}
            />
          </label>

          <p className="auth__hint">
            Te mandamos un enlace de confirmación. El correo no se muestra a nadie más ni
            se usa para otra cosa.
          </p>

          <button className="btn" type="submit" disabled={!canSubmit}>
            {busy ? "Guardando…" : "Guardar y confirmar"}
          </button>
        </form>

        {done && !error && (
          <p className="auth__hint" role="status">
            Listo. Revisa tu correo para confirmar la dirección.
          </p>
        )}

        {error && (
          <p className="auth__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
